import React from 'react';
import { ESSButton } from 'volto-essvoltocomponents/components/Atoms/ESSButton';
import './Pagination.scss';

export interface PaginationProps {
  /**
   * The current active page (1-based index).
   */
  currentPage: number;
  /**
   * The total number of pages available.  Must be at least 1.
   */
  totalPages: number;
  /**
   * Callback fired when the user selects a different page.  Receives the
   * 1-based page number selected.
   */
  onPageChange: (page: number) => void;
  /**
   * Maximum number of page links to display at once (excluding prev/next).  If
   * omitted, defaults to 3.  When the total number of pages exceeds this
   * number, ellipses (`…`) will be shown to indicate skipped pages.
   */
  maxButtons?: number;
  /**
   * Optional className applied to the top-level pagination element.
   */
  className?: string;
}

/**
 * Pagination component for navigating between pages of content.  It renders
 * numbered page buttons along with previous/next controls.  When there are
 * more pages than `maxButtons`, it collapses the range of displayed page
 * numbers using ellipses.  This implementation is intentionally simple; for
 * very large numbers of pages or more advanced behaviour (like jumping
 * multiple ranges), adapt accordingly.
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  maxButtons = 3,
  className,
}) => {
  // Ensure sensible defaults
  const current = Math.max(1, Math.min(currentPage, totalPages));
  const max = Math.max(1, totalPages);
  const buttonsToShow = Math.max(1, maxButtons);

  // Calculate page range to display (including current, around it)
  const half = Math.floor(buttonsToShow / 2);
  let start = Math.max(1, current - half);
  let end = start + buttonsToShow - 1;
  if (end > max) {
    end = max;
    start = Math.max(1, end - buttonsToShow + 1);
  }

  const pages: (number | string)[] = [];
  // Always show first page if not in range
  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push('ellipsis-start');
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  // Always show last page if not in range
  if (end < max) {
    if (end < max - 1) pages.push('ellipsis-end');
    pages.push(max);
  }

  // Helper to render a single page element
  const renderPageItem = (item: number | string, index: number) => {
    if (typeof item === 'number') {
      const isActive = item === current;
      return (
        <li key={index} className="pagination__item" role="presentation">
          <ESSButton
            variant={isActive ? 'positive' : 'negative'}
            text={String(item)}
            className={`pagination__button${isActive ? ' pagination__button--active' : ''}`}
            onClick={() => onPageChange(item)}
            aria-current={isActive ? 'page' : undefined}
          />
        </li>
      );
    }
    // Render ellipsis
    return (
      <li key={index} className="pagination__item pagination__item--ellipsis" role="presentation">
        <span className="pagination__ellipsis">…</span>
      </li>
    );
  };

  return (
    <nav
      className={['pagination', className].filter(Boolean).join(' ')}
      role="navigation"
      aria-label="Pagination"
    >
      <ul className="pagination__list">
        {/* Previous button */}
        <li className="pagination__item" role="presentation">
          <ESSButton
            variant="negative"
            text="«"
            className="pagination__button pagination__button--prev"
            onClick={() => onPageChange(Math.max(1, current - 1))}
            disabled={current === 1}
            aria-label="Previous page"
          />
        </li>
        {pages.map(renderPageItem)}
        {/* Next button */}
        <li className="pagination__item" role="presentation">
          <ESSButton
            variant="negative"
            text="»"
            className="pagination__button pagination__button--next"
            onClick={() => onPageChange(Math.min(max, current + 1))}
            disabled={current === max}
            aria-label="Next page"
          />
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
